import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import * as z from "zod/v4";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FilterIcon } from "lucide-react";
import {
  DateRangeFilter,
  EntityFilters,
  MatrixTable,
  SummaryTable,
} from "./-components";
import type { DetectionsParams } from "@/lib/queries/desktop-statistics";
import type { MatrixRow, SummaryRow } from "./-components";
import {
  useDepartmentEntityDetections,
  useEntityCategories,
} from "@/lib/queries/desktop-statistics";
import { departmentsAllQueryOptions } from "@/lib/queries/departments";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

function formatDate(d: Date) {
  return d.toISOString().slice(0, 10);
}
const DEFAULT_END_DATE = formatDate(new Date());
const DEFAULT_START_DATE = formatDate(
  new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
);

const searchSchema = z.object({
  start_date: z.string().default(DEFAULT_START_DATE).catch(DEFAULT_START_DATE),
  end_date: z.string().default(DEFAULT_END_DATE).catch(DEFAULT_END_DATE),
  department_id: z.coerce.number().optional().catch(undefined),
  entity_category: z.string().optional().catch(undefined),
  entity_id: z.coerce.number().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/edge-device-statistics/")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  staticData: {
    permissionGuards: [
      {
        resource: "statistics",
      },
    ],
  },
});

function RouteComponent() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const categoriesQuery = useEntityCategories();
  const detectionsQuery = useDepartmentEntityDetections(search);

  const isLoading = categoriesQuery.isLoading || detectionsQuery.isLoading;
  const isError = categoriesQuery.isError || detectionsQuery.isError;

  const categories = categoriesQuery.data?.categories ?? [];
  const departments = detectionsQuery.data?.departments ?? [];

  const visibleCategories = useMemo(() => {
    if (!search.entity_category) return categories;
    const filtered = categories
      .filter((c) => c.name === search.entity_category)
      .map((c) => ({
        ...c,
        entities: search.entity_id
          ? c.entities.filter((e) => e.entity_id === search.entity_id)
          : c.entities,
      }));
    return filtered;
  }, [categories, search.entity_category, search.entity_id]);

  const matrixRows = useMemo<Array<MatrixRow>>(
    () =>
      departments.map((d) => ({
        id: d.department_id,
        name: d.department_name,
        total: d.total,
        entities: d.entities,
        href: `/edge-device-statistics/${d.department_id}`,
      })),
    [departments],
  );

  const summaryRows = useMemo<Array<SummaryRow>>(
    () =>
      departments.map((d) => ({
        id: d.department_id,
        name: d.department_name,
        total: d.total,
        critical: d.critical,
        href: `/edge-device-statistics/${d.department_id}`,
      })),
    [departments],
  );

  const activeFilterCount = [
    search.department_id,
    search.entity_category,
    search.entity_id,
  ].filter(Boolean).length;

  function updateSearch(updates: Partial<typeof search>) {
    navigate({ to: ".", search: { ...search, ...updates } });
  }

  return (
    <div className="flex w-full flex-col gap-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-3xl font-bold">Edge Device Statistics</h1>
        <div className="flex items-center gap-2">
          <DateRangeFilter
            startDate={search.start_date}
            endDate={search.end_date}
            defaultStartDate={DEFAULT_START_DATE}
            defaultEndDate={DEFAULT_END_DATE}
            onApply={(start_date, end_date) =>
              updateSearch({ start_date, end_date })
            }
          />
          <FilterPopover
            search={search}
            categories={categories}
            onApply={(filters) => updateSearch(filters)}
            activeCount={activeFilterCount}
          />
        </div>
      </div>

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      )}

      {isError && (
        <div className="flex h-64 flex-col items-center justify-center gap-2 text-muted-foreground">
          <p>Error loading data.</p>
          {categoriesQuery.error && (
            <pre className="max-w-xl text-xs text-destructive">
              categories: {String(categoriesQuery.error)}
            </pre>
          )}
          {detectionsQuery.error && (
            <pre className="max-w-xl text-xs text-destructive">
              detections: {String(detectionsQuery.error)}
            </pre>
          )}
        </div>
      )}

      {!isLoading && !isError && (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <MatrixTable
              categories={visibleCategories}
              rows={matrixRows}
              rowLabel="Department"
            />
          </div>
          <SummaryTable rows={summaryRows} nameLabel="Department" />
        </>
      )}
    </div>
  );
}

// --- Filter Popover (department + entity filters) ---

type FilterValues = Pick<
  DetectionsParams,
  "department_id" | "entity_category" | "entity_id"
>;

function FilterPopover({
  search,
  categories,
  onApply,
  activeCount,
}: {
  search: FilterValues;
  categories: Parameters<typeof EntityFilters>[0]["categories"];
  onApply: (filters: FilterValues) => void;
  activeCount: number;
}) {
  const [open, setOpen] = useState(false);
  const [departmentId, setDepartmentId] = useState(
    search.department_id?.toString() ?? "",
  );
  const [entityCategory, setEntityCategory] = useState(
    search.entity_category ?? "",
  );
  const [entityId, setEntityId] = useState(search.entity_id?.toString() ?? "");

  const departmentsQuery = useQuery(departmentsAllQueryOptions());

  function handleOpenChange(val: boolean) {
    if (val) {
      setDepartmentId(search.department_id?.toString() ?? "");
      setEntityCategory(search.entity_category ?? "");
      setEntityId(search.entity_id?.toString() ?? "");
    }
    setOpen(val);
  }

  function handleApply() {
    onApply({
      department_id: departmentId ? Number(departmentId) : undefined,
      entity_category: entityCategory || undefined,
      entity_id: entityId ? Number(entityId) : undefined,
    });
    setOpen(false);
  }

  function handleReset() {
    setDepartmentId("");
    setEntityCategory("");
    setEntityId("");
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="relative gap-2">
          <FilterIcon className="h-4 w-4" />
          Filters
          {activeCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="end">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold">Filters</p>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground"
              onClick={handleReset}
            >
              Reset all
            </Button>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Department
            </label>
            <Select
              value={departmentId}
              onValueChange={(v) => setDepartmentId(v === "_all" ? "" : v)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="All departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All departments</SelectItem>
                {departmentsQuery.data?.map((d) => (
                  <SelectItem key={d.id} value={d.id.toString()}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <EntityFilters
            categories={categories}
            entityCategory={entityCategory}
            entityId={entityId}
            onCategoryChange={setEntityCategory}
            onEntityChange={setEntityId}
          />

          <div className="flex gap-2 pt-1">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button className="flex-1" onClick={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
